"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import {
  absoluteAssetUrl,
  blogPostUrl,
  resolveCategoryName,
  type BlogCategory,
  type BlogPostStory,
} from "@/lib/blog";
import type { Region } from "@/lib/region";

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function BlogCard({
  post,
  region,
}: {
  post: BlogPostStory;
  region: Region;
}) {
  const c = post.content || {};
  const href = blogPostUrl(post.slug, region);
  return (
    <li>
      <Link
        href={href}
        className="group flex h-full flex-col gap-4 rounded-[18px] focus-visible:outline-offset-4 lg:rounded-[20px]"
      >
        <div className="relative aspect-4/3 w-full overflow-hidden rounded-[18px] bg-dusk-01 lg:rounded-[20px]">
          {c.featured_image?.filename ? (
            <Image
              src={absoluteAssetUrl(c.featured_image.filename) || ""}
              alt={c.featured_image.alt || post.name}
              fill
              sizes="(min-width: 1115px) 360px, (min-width: 720px) 50vw, 100vw"
              className="object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-linear-to-br from-dusk-02 to-purple-01" />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-3">
          <h2 className="text-h6 font-bold leading-[1.2] text-purple-05">
            {c.title || post.name}
          </h2>
          <span className="mt-auto inline-flex items-center text-body font-extrabold leading-[1.054] text-purple-03 group-hover:underline">
            Read more
          </span>
        </div>
      </Link>
    </li>
  );
}

export function BlogIndexPage({
  posts,
  region,
  categories: datasourceCategories = [],
}: {
  posts: BlogPostStory[];
  region: Region;
  categories?: BlogCategory[];
}) {
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          posts
            .map((p) => p.content?.category)
            .filter((c): c is string => Boolean(c)),
        ),
      ),
    [posts],
  );

  // Filter lives in client state only — never in the URL.
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filtered = activeCategory
    ? posts.filter((p) => p.content?.category === activeCategory)
    : posts;

  const flagged = filtered.find((p) => p.content?.featured === true);
  const featured = flagged ?? filtered[0];
  const rest = filtered.filter((p) => p.uuid !== featured?.uuid);

  return (
    <article>
      <section className="bg-dusk-01">
        <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(3rem,6vw,5.5rem)] pb-[clamp(2.5rem,5vw,3.5rem)]">
          <div className="mx-auto max-w-[960px] text-center">
            <h1 className="text-[clamp(2.5rem,5vw,4rem)] font-bold leading-[1.08] tracking-[-0.01em] text-purple-05">
              IVF Blog &amp; Fertility Insights
            </h1>
            <p className="mx-auto mt-4 max-w-[760px] text-body leading-normal text-black/70 lg:text-h6">
            Our fertility blog brings together patient journeys, expert guidance, and the latest news from Thérapie Fertility. From egg freezing guides and real patient stories to clinic updates and treatment advice, find the insights that matter most to you.
            </p>
          </div>

          {categories.length > 0 && (
            <div className="mx-auto mt-8 flex max-w-[820px] flex-wrap items-center justify-center gap-2 lg:mt-10">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                aria-pressed={!activeCategory}
                className={`inline-flex h-10 items-center rounded-full px-4 text-button font-semibold transition-colors ${
                  !activeCategory
                    ? "bg-purple-03 text-white"
                    : "bg-white text-purple-05 ring-1 ring-black/10 hover:bg-dusk-02"
                }`}
              >
                All
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  aria-pressed={activeCategory === cat}
                  className={`inline-flex h-10 items-center rounded-full px-4 text-button font-semibold transition-colors ${
                    activeCategory === cat
                      ? "bg-purple-03 text-white"
                      : "bg-white text-purple-05 ring-1 ring-black/10 hover:bg-dusk-02"
                  }`}
                >
                  {resolveCategoryName(cat, datasourceCategories) || cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {filtered.length === 0 ? (
        <div className="bg-white">
          <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,5vw,4.5rem)]">
            <p className="rounded-[16px] border border-dashed border-black/15 p-10 text-center text-button text-black/55">
              No posts in this category yet — check back soon.
            </p>
          </div>
        </div>
      ) : (
        <>
          {!activeCategory && featured && (
            <section className="bg-white">
              <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[clamp(3rem,5vw,4.5rem)] pb-[clamp(3rem,5vw,4.5rem)]">
                <FeaturedCard
                  post={featured}
                  region={region}
                  categories={datasourceCategories}
                />
              </div>
            </section>
          )}
          {(activeCategory ? filtered : rest).length > 0 && (
            <section
              className={
                !activeCategory && featured ? "bg-dusk-01" : "bg-white"
              }
            >
              <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(3rem,6vw,5rem)]">
                <ul className="grid gap-x-8 gap-y-12 sm:grid-cols-2 md:grid-cols-3">
                  {(activeCategory ? filtered : rest).map((post) => (
                    <BlogCard key={post.uuid} post={post} region={region} />
                  ))}
                </ul>
              </div>
            </section>
          )}
        </>
      )}
    </article>
  );
}

function FeaturedCard({
  post,
  region,
  categories = [],
}: {
  post: BlogPostStory;
  region: Region;
  categories?: BlogCategory[];
}) {
  const c = post.content || {};
  const href = blogPostUrl(post.slug, region);
  const category = c.category
    ? resolveCategoryName(c.category, categories)
    : "";

  return (
    <Link
      href={href}
      className="group flex flex-col gap-8 rounded-[18px] focus-visible:outline-offset-4 md:flex-row md:items-center md:gap-12 lg:rounded-[24px] xl:gap-[104px]"
    >
      <div className="relative aspect-16/10 w-full overflow-hidden rounded-[18px] bg-dusk-01 md:flex-1 md:shrink-0 lg:rounded-[24px] xl:h-[368px] xl:w-[492px] xl:flex-initial">
        {c.featured_image?.filename ? (
          <Image
            src={absoluteAssetUrl(c.featured_image.filename) || ""}
            alt={c.featured_image.alt || post.name}
            fill
            sizes="(min-width: 1280px) 492px, (min-width: 768px) 50vw, 100vw"
            className="object-cover"
            priority
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-dusk-02 to-purple-01" />
        )}
      </div>
      <div className="flex flex-col gap-6 md:flex-1 md:shrink-0 xl:w-[510px] xl:flex-initial">
        {category && (
          <span className="text-caption font-bold tracking-wider text-purple-03 uppercase">
            {category}
          </span>
        )}
        <div className="flex flex-col gap-3 text-purple-05 lg:gap-5">
          <h2 className="text-h4 font-bold leading-[1.12] lg:text-h2 lg:leading-[1.054]">
            {c.title || post.name}
          </h2>
        </div>
        <span className="inline-flex w-fit items-center gap-[7px] text-body font-extrabold leading-[1.054] text-purple-03 group-hover:underline">
          Read story
          <ArrowIcon />
        </span>
      </div>
    </Link>
  );
}
